import { areAddressesTheSame } from "@insite/client-framework/Common/CustomerSelectors";
import { BaseAddressModel, BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";

const addressLocationKey = (address?: BaseAddressModel) =>
    `${address?.state?.id ?? ""}|${address?.postalCode ?? ""}|${address?.city ?? ""}`;

/**
 * Guests see both billing and shipping forms. On return visits the persisted shipTo
 * repopulates billing while the user may edit shipping. Pick the form that actually changed.
 */
export const resolveGuestCheckoutAddress = (
    billing?: BillToModel,
    shipping?: ShipToModel,
    storeShipTo?: ShipToModel,
): BillToModel | ShipToModel | undefined => {
    if (!billing && !shipping) {
        return undefined;
    }

    if (!storeShipTo) {
        return billing ?? shipping;
    }

    const billingKey = addressLocationKey(billing);
    const shippingKey = addressLocationKey(shipping);
    const storeKey = addressLocationKey(storeShipTo);

    const billingChanged = !!billing && !areAddressesTheSame(billing, storeShipTo);
    const shippingChanged = !!shipping && !areAddressesTheSame(shipping, storeShipTo);

    if (shippingKey !== storeKey && billingKey === storeKey) {
        return shipping;
    }

    if (billingKey !== storeKey && shippingKey === storeKey) {
        return billing;
    }

    if (shippingChanged && !billingChanged) {
        return shipping;
    }

    if (billingChanged && !shippingChanged) {
        return billing;
    }

    if (shippingChanged && billingChanged) {
        return shipping;
    }

    return billing ?? shipping;
};

export const resolveFormAddressForValidation = (
    isGuest: boolean,
    useBillingAddress: boolean,
    billing?: BillToModel,
    shipping?: ShipToModel,
    storeShipTo?: ShipToModel,
): BaseAddressModel | undefined => {
    if (isGuest) {
        return resolveGuestCheckoutAddress(billing, shipping, storeShipTo);
    }

    return useBillingAddress ? billing : shipping;
};
